import { css } from '@emotion/react';
import type { ButtonHTMLAttributes, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import IconDeleteForever from '~icons/material-symbols/delete-forever';
import IconDeleteForeverOutline from '~icons/material-symbols/delete-forever-outline';

import { theme } from '@adflow/frontend/ui-theme';

/**
 * Styles that position an element in the center of its parent
 * without affecting the layout of other elements.
 *
 * @todo Should this be moved to somewhere so that it can be reusable?
 */
const centeredAbsolute = ({
	width,
	height,
}: {
	width: string;
	height: string;
}) =>
	css({
		width,
		height,
		position: 'absolute',
		top: `calc(50% - ${height} / 2)`,
		left: `calc(50% - ${width} / 2)`,
	});

const styles = {
	button: css({
		position: 'relative',
		width: '2rem',
		height: '2rem',
		'&:active': {
			filter: 'brightness(81%)',
		},
		'&[data-disabled=true]': css({
			color: theme.colors.action.disabledBackground,
			cursor: 'default',

			svg: css({
				color: theme.colors.action.disabledBackground,
			}),
			'svg:active': { filter: 'none' },
		}),
	}),
	buttonSmall: css({ width: '1.25rem', height: '1.25rem' }),
	icon: css(
		{
			color: theme.colors.palettes.delete.main,
		},
		centeredAbsolute({ width: '28px', height: '28px' })
	),

	iconSmall: css(
		{
			color: theme.colors.palettes.delete.main,
		},
		centeredAbsolute({ width: '20px', height: '20px' })
	),

	iconNormal: css({
		'button:hover:not([data-disabled=true]) > &, button:focus-visible:not([data-disabled=true]) > &':
			{
				display: 'none',
			},
	}),
	iconHoverFocus: css({
		display: 'none',
		'button:hover:not([data-disabled=true]) > &, button:focus-visible:not([data-disabled=true]) > &':
			{
				display: 'initial',
			},
	}),
};

export type ButtonIconTrashForeverProps =
	ButtonHTMLAttributes<HTMLButtonElement> & {
		small?: boolean;
		testid?: string;
	};

export const ButtonIconTrashForever = forwardRef(
	function ButtonIconTrashForeverRender(
		{ small = false, ...props }: ButtonIconTrashForeverProps,
		ref: ForwardedRef<HTMLButtonElement>
	) {
		return (
			<button
				css={[styles.button, small && styles.buttonSmall]}
				type="button"
				{...props}
				ref={ref}
				data-testid={props.testid ?? 'button-icon-trash-forever'}
			>
				<IconDeleteForeverOutline
					css={[styles.icon, small && styles.iconSmall, styles.iconNormal]}
				/>

				<IconDeleteForever
					css={[styles.icon, small && styles.iconSmall, styles.iconHoverFocus]}
				/>
			</button>
		);
	}
);
